import React from 'react';
import { format } from 'date-fns';
import pluralize from 'pluralize';
import clsx from 'clsx';

import Subtitle from '../../../ui/Subtitle';
import Spinner from '../../../ui/Spinner';

import * as Styles from './CancellationStyles';
import { usePrescriptionPdfDownload } from '../../../../hooks/usePrescriptionPdfDownload';

import { DispensationItemInterface, PrescriptionItemInterface } from '../../../../interfaces/prescription';
import { PermissionsInterface } from '../../../../interfaces/drugstore';
import ItemsStatusInterface from '../../../../interfaces/itemsStatus';
import {
  trackClickCancelDispensation,
  trackClickDownloadIndividualPrescription,
} from '../../../../monitoring/interaction/prescription-detail';
import { useTrackingProperties } from '../../../../monitoring/utils';

interface DispensedItemsListProps {
  items: PrescriptionItemInterface[];
  permissions: PermissionsInterface;
  itemsStatus: ItemsStatusInterface[];
  loadingItem: number | boolean;
  cancelDispensation: Function;
  prescriptionId: number;
  prescriptionToken: string;
}

function DispensedItemsList({
  items,
  permissions,
  itemsStatus,
  loadingItem,
  cancelDispensation,
  prescriptionId,
  prescriptionToken,
}: DispensedItemsListProps): JSX.Element {
  const { downloadIndividualPdf, isLoading } = usePrescriptionPdfDownload(prescriptionToken);
  const { drugstoreId, chainId, drugstoreName, chainName, drugstoreUF } = useTrackingProperties();

  const checkOwner = (dispensation: DispensationItemInterface): boolean => {
    const authenticatedChainId = permissions.current_drugstore ? permissions.current_drugstore.chain.id : null;

    const dispensationChainId = dispensation.drugstore ? dispensation.drugstore.chain_id : null;

    return dispensationChainId === authenticatedChainId;
  };

  const getQuantityInfo = ({ itemId, quantity }: { itemId: number; quantity: number }): string => {
    const statusItem = itemsStatus.find((status) => status.item_id === itemId);
    const prescriptionItem = items.find((item) => item.id === itemId);
    const quantityValue = quantity === null ? prescriptionItem?.original_quantity : quantity;

    if (statusItem && prescriptionItem) {
      const unit = prescriptionItem?.unit ?? 'unidade';
      return ` - ${quantityValue} ${pluralize(unit, quantityValue)} de ${prescriptionItem.original_quantity}`;
    }

    return '';
  };

  const isIndividualPdfAvailable = (item: PrescriptionItemInterface): boolean =>
    Boolean(item.dispensation.document.pdf_access);

  const handleDownloadPdf = async ({ item }: { item: PrescriptionItemInterface }): Promise<void> => {
    trackClickDownloadIndividualPrescription({
      prescriptionId,
      drugstoreId,
      chainId,
      drugstoreName,
      chainName,
      drugstoreUF,
    });

    await downloadIndividualPdf({
      loadingKey: item.id,
      getPdfAccess: () => item.dispensation.document.pdf_access,
      getRefreshedPdfAccess: (refreshedPrescription) => {
        const refreshedItem = refreshedPrescription.items.find((prescriptionItem) => prescriptionItem.id === item.id);
        return refreshedItem?.dispensation.document.pdf_access ?? null;
      },
      errorTitle: 'Erro ao baixar prescrição individual',
    });
  };

  function handleCancelDispensation({ dispensationItem }: { dispensationItem: DispensationItemInterface }): void {
    trackClickCancelDispensation({
      prescriptionId,
      drugstoreId,
      chainId,
      drugstoreName,
      chainName,
      drugstoreUF,
    });
    cancelDispensation(dispensationItem.id);
  }

  function getDownloadIndividualPdfTitle(item: PrescriptionItemInterface): string {
    if (isLoading(item.id)) {
      return 'Baixando prescrição individual';
    }

    if (!isIndividualPdfAvailable(item)) {
      return `O medicamento ${item.name} não dispõe de PDF para download`;
    }

    return `Clique para realizar o download do PDF do medicamento ${item.name}`;
  }

  return (
    <>
      <Subtitle>Itens dispensados</Subtitle>
      <Styles.List>
        {items.map((item) =>
          item.dispensation.data
            .filter((dispensationItem) => dispensationItem.status !== 'blocked')
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map((dispensationItem) => (
              <Styles.Row key={dispensationItem.id}>
                <Styles.ItemContainer>
                  <Styles.ItemName>{item.name}</Styles.ItemName>
                  <Styles.ItemDate>
                    {format(dispensationItem.date, 'dd/MM/yyyy HH:mm:ss')}
                    {getQuantityInfo({ itemId: item.id, quantity: dispensationItem.quantity })}
                  </Styles.ItemDate>
                </Styles.ItemContainer>
                <Styles.Actions>
                  {checkOwner(dispensationItem) && (
                    <>
                      <Styles.Button
                        className='tracking-cancel-dispensation'
                        onClick={() => handleCancelDispensation({ dispensationItem })}
                        size='small'
                        hoverColor='alert'
                        disabled={loadingItem === dispensationItem.id}
                      >
                        {loadingItem && loadingItem === dispensationItem.id ? (
                          <Spinner size='14px' />
                        ) : (
                          'Cancelar'
                        )}
                      </Styles.Button>
                      <Styles.Button
                        aria-disabled={!isIndividualPdfAvailable(item) || isLoading(item.id)}
                        className={clsx(
                          `tracking-download-pdf-dispensation-item`,
                          {
                            'disabled-link': !isIndividualPdfAvailable(item),
                            loading: isLoading(item.id),
                          }
                        )}
                        disabled={isLoading(item.id) || !isIndividualPdfAvailable(item)}
                        onClick={() => {
                          handleDownloadPdf({ item });
                        }}
                        title={getDownloadIndividualPdfTitle(item)}
                      >
                        <span className='text'>Baixar prescrição individual</span>
                        <div className='spinner'> <Spinner size='14px' /></div>
                      </Styles.Button>
                    </>
                  )}
                </Styles.Actions>
              </Styles.Row>
            )),
        )}
      </Styles.List>
    </>
  );
}

export default DispensedItemsList;
